import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash";
import { toast } from "sonner";

import { queryClient } from "App";
import { addResizedColumns } from "hooks/useTableConfig";
import { SMTP_ACCOUNTS_TABLE_CONSTANTS } from "routes/smtp_accounts/SmtpAccountsTableV3";
import { getSmtpAccountsTableColumns } from "routes/smtp_accounts/SmtpAccountsTableColumnsAndFilters";
import UserService from "services/User";
import SmtpAccountService from "services/SmtpAccounts";

export const invalidateSmtpAccountsQuery = async () => {
  await queryClient.invalidateQueries({
    queryKey: [SMTP_ACCOUNTS_TABLE_CONSTANTS.queryKey],
  });
};

export const useSmtpAccountsTable = () => {
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const handleSetAsDefault = useCallback(async (configId, isDefault) => {
    try {
      await SmtpAccountService.updateSmtpAccount(configId, { isDefault });
      await invalidateSmtpAccountsQuery();
      toast.success("SMTP account updated");
    } catch (error) {
      toast.error(error.message || "Failed to update SMTP account");
    }
  }, []);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getSmtpAccountsTableColumns({ handleSetAsDefault });
    const smtpAccountsTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${SMTP_ACCOUNTS_TABLE_CONSTANTS.tableName}`,
      {}
    );
    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: smtpAccountsTableSettings?.resized_columns || {},
    });
    return withResizedColumns;
  }, [tableSettings, isTableSettingsFetched, handleSetAsDefault]);

  return { columns };
};
